import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { BackPill } from './BackPill';
import { motion, AnimatePresence } from 'framer-motion';
import { openPicker } from '../../utils/timePicker';
import { schedulePush } from '../AuraRing';
import { X, Plus, Trash2, ChevronLeft, ChevronDown, Flame } from 'lucide-react';
import { stopSignatureTone } from '../../utils/codexAlarms';
import { pullEnergyNotesIntoCodex, scheduleCodexEnergyPush } from '../../utils/energySync';
import { vibrate as nativeVibrate } from '../../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   THE AURA CODEX — Divine Energy Engine
   • Master Aura Artifact (top): brushed-titanium concentric rings
     around a translucent plasma sphere — 4 tiers (baseline / awakened
     / resonance / divine) drive palette, ring spin, halo, lightning.
   • Timeline of Ascension (mid): a glowing cyan neon wire bisecting
     the screen carrying 5 small hollow chrome nodes — Vitality,
     Essence, Focus, Zen, Sanctuary. Press-and-hold a node 1.5 s to
     fuse it (contracting ring + vibration rumble + ignite pinpoint +
     kinetic light streak surging up the wire into the master orb).
   • Codex of Intent (bottom-input): natural phrase ("Gym at 06:00 AM")
     keyword-matches a pillar and appends a stylised intent.
   • 5 Pillar Cards (bottom grid): each card is a premium pillar-
     coloured aura ball with its name + role; tap → full-page Energy
     Box panel showing the sacred passage and the per-pillar intent
     list with an add input and a Fuse / Unseal action.
   • 24-h cycle: localStorage with auto-purge at local midnight.
   • Zero cloud cost — pure CSS / SVG visuals end-to-end.
   ═══════════════════════════════════════════════════════════════════ */

/* 6 Empire Pillars — each auto-fuses when the user completes today's
   ritual in the source tile. No manual fuse: tapping a ball opens the
   pillar's description panel (EnergyBox) that guides the user to the
   correct tile.
   Palettes:
     Power    ↔ ruby       (was Vitality)
     Fuel     ↔ emerald    (was Essence)
     Force    ↔ sapphire   (was Focus)
     Energy   ↔ amethyst   (was Zen)
     Destiny  ↔ cobalt     (was Sanctuary)
     Aegis    ↔ orange     (NEW)                                          */
const PILLARS = [
  {
    id: 'power',
    label: 'Power',
    role: 'Physical Protocol',
    passage: 'Daily physical execution — the Power tile forges reps into strength.',
    sourceTile: 'Power',
    keywords: ['gym','run','workout','exercise','train','walk','yoga','sport','lift','cycle','swim','stretch'],
    palette: { core: '#FFB0B8', mid: '#D63249', outer: '#4A0A14', halo: 'rgba(255,80,100,0.7)', accent: '#FF6478' },
  },
  {
    id: 'fuel',
    label: 'Fuel',
    role: 'Nutrition · Kcal Cycle',
    passage: 'High-grade clean nutrition — the Fuel tile seals the day when your kcal target is fused.',
    sourceTile: 'Fuel',
    keywords: ['food','eat','meal','water','hydrate','nutrition','breakfast','lunch','dinner','fast','drink','protein','vitamin'],
    palette: { core: '#B8FFD0', mid: '#1FA859', outer: '#08361A', halo: 'rgba(60,210,120,0.7)', accent: '#5BE08A' },
  },
  {
    id: 'force',
    label: 'Force',
    role: 'Yoga & Breath',
    passage: 'Yoga forging, breath discipline — the Force tile registers each Fuse-locked session.',
    sourceTile: 'Force',
    keywords: ['yoga','breath','asana','pranayama','stretch','flow','poise'],
    palette: { core: '#CFE9FF', mid: '#1B6FE3', outer: '#0A1A45', halo: 'rgba(93,212,255,0.75)', accent: '#5DD4FF' },
  },
  {
    id: 'energy',
    label: 'Energy',
    role: 'Divine Breath',
    passage: 'Divine breath cycles — the Energy tile writes today\'s completed cycles into the vessel.',
    sourceTile: 'Energy',
    keywords: ['meditate','reflect','journal','pray','breathe','silence','still','calm','grateful','hobby','art'],
    palette: { core: '#E0C0FF', mid: '#7B38D8', outer: '#220A55', halo: 'rgba(186,120,255,0.7)', accent: '#B978FF' },
  },
  {
    id: 'destiny',
    label: 'Destiny',
    role: 'Manifestation & Fate',
    passage: 'Manifestation seals — the Destiny tile inscribes each daily codex.',
    sourceTile: 'Destiny',
    keywords: ['destiny','manifest','vision','path','fate','intent'],
    palette: { core: '#C8D8FF', mid: '#3550C8', outer: '#0A1238', halo: 'rgba(140,170,255,0.7)', accent: '#7C9BFF' },
  },
  {
    id: 'aegis',
    label: 'Aegis',
    role: 'Skin · Hair · Regimen',
    passage: 'Skin & hair regimen — long-press any Aegis calendar day to seal today\'s ritual.',
    sourceTile: 'Aegis',
    keywords: ['skin','hair','regimen','care','mask','oil','cleanse'],
    palette: { core: '#FFD9A8', mid: '#E67E22', outer: '#4A2408', halo: 'rgba(230,126,34,0.75)', accent: '#FFB265' },
  },
];

const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const STORAGE_KEY = 'aura_codex_state_v1';

const blankState = () => ({
  date: todayKey(),
  pillars: PILLARS.reduce((acc, p) => {
    /* `window` gates fusing per pillar. `lastFusedAt` records the
       wall-clock ms when the user fused this pillar so we can colour
       the master orb with the MOST RECENTLY fused pillar's palette. */
    acc[p.id] = { fused: false, tasks: [], window: { start: '', end: '' }, lastFusedAt: 0 };
    return acc;
  }, {}),
});

/* New day: reset fuse progress but keep each pillar's alarm schedule. */
const freshDay = (prev) => {
  const b = blankState();
  PILLARS.forEach((p) => {
    const old = prev?.pillars?.[p.id];
    if (old?.alarms) b.pillars[p.id].alarms = old.alarms;
    if (old?.alarmNoteIds) b.pillars[p.id].alarmNoteIds = old.alarmNoteIds;
  });
  return b;
};

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return blankState();
    const s = JSON.parse(raw);
    if (s.date !== todayKey()) return freshDay(s);      // midnight purge
    PILLARS.forEach((p) => {
      if (!s.pillars[p.id]) s.pillars[p.id] = { fused: false, tasks: [], window: { start: '', end: '' }, lastFusedAt: 0 };
      if (!s.pillars[p.id].window) s.pillars[p.id].window = { start: '', end: '' };
      if (typeof s.pillars[p.id].lastFusedAt !== 'number') s.pillars[p.id].lastFusedAt = 0;
    });
    return s;
  } catch (_) { return blankState(); }
};

const saveState = (s) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_) { /* ignore */ }
  schedulePush();
};

/* Convert "HH:MM" → minutes since midnight (or NaN if blank/invalid). */
const hhmmToMinutes = (str) => {
  if (!str || typeof str !== 'string') return NaN;
  const [h, m] = str.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  return h * 60 + m;
};

/* HH:MM (24h) → "h:MM AM/PM" for display inside alarm slots. */
const formatTime12 = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
};

/* Return both inside-state AND seconds-until-end for a pillar window.
   Used to drive the per-pillar warning pulse + 60 s countdown. */
const windowStatus = (win, nowSecs) => {
  const sMin = hhmmToMinutes(win?.start);
  const eMin = hhmmToMinutes(win?.end);
  if (Number.isNaN(sMin) || Number.isNaN(eMin)) return { inside: true, untilEnd: Infinity };
  if (sMin === eMin) return { inside: true, untilEnd: Infinity };
  const s = sMin * 60;
  const e = eMin * 60;
  if (s < e) {
    return { inside: nowSecs >= s && nowSecs <= e, untilEnd: e - nowSecs };
  }
  /* Overnight window — wraps past midnight. */
  if (nowSecs >= s) return { inside: true, untilEnd: 86400 - nowSecs + e };
  if (nowSecs <= e) return { inside: true, untilEnd: e - nowSecs };
  return { inside: false, untilEnd: -1 };
};

/* Return true if `nowMins` falls inside the given window. (kept for back-compat) */
const isInWindow = (win, nowMins) => {
  const nowSecs = nowMins * 60;
  return windowStatus(win, nowSecs).inside;
};

const fmtWindow = (win) => {
  const s = hhmmToMinutes(win?.start);
  const e = hhmmToMinutes(win?.end);
  if (Number.isNaN(s) || Number.isNaN(e)) return 'Anytime';
  return `${win.start} – ${win.end}`;
};

/* (Codex of Intent removed — pillars are now inscribed directly via each
   pillar's Energy Box, so the keyword auto-router is no longer needed.) */

const computeTier = (fusedCount) => {
  const volume = (fusedCount / PILLARS.length) * 100;
  if (volume >= 100) return { tier: 'divine',    volume };
  if (volume >= 50)  return { tier: 'resonance', volume };
  if (volume > 0)    return { tier: 'awakened',  volume };
  return { tier: 'baseline', volume };
};

/* ─────────────────────────────────────────────────────────────────
   AURA ARTIFACT — concentric chrome rings + plasma sphere
   Supports an optional `palette` override so pillar cards render the
   same artifact in their own pillar colour. `beamAlways` keeps the
   halo & accent ring lit even at baseline tier (so dormant pillar
   cards still feel premium).
   ───────────────────────────────────────────────────────────────── */
const AuraArtifactImpl = ({ tier = 'baseline', volume = 0, size = 220, palette: paletteOverride, beamAlways = false, warning = false, warningPalette }) => {
  const spinDur = tier === 'divine' ? 0 : tier === 'resonance' ? 6 : tier === 'awakened' ? 14 : 28;
  const baseTier = {
    baseline:  { core: '#6A3CC0', mid: '#3B1E70', outer: '#1A0A38', halo: 'rgba(106,60,192,0.35)' },
    awakened:  { core: '#9C5BFF', mid: '#5028B8', outer: '#1F0A4A', halo: 'rgba(156,91,255,0.55)' },
    resonance: { core: '#5DD4FF', mid: '#1B6FE3', outer: '#10245E', halo: 'rgba(93,212,255,0.7)'  },
    divine:    { core: '#FFF6D0', mid: '#FFD66A', outer: '#A26A0E', halo: 'rgba(255,235,150,0.95)' },
  }[tier];
  const palette = paletteOverride || baseTier;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Outer halo */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${palette.halo} 0%, transparent 70%)`,
          filter: 'blur(18px)',
          transform: `scale(${tier === 'divine' ? 1.7 : 1.35})`,
          opacity: tier === 'baseline' && !beamAlways ? 0.4 : 0.95,
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}
      />

      {/* 60-s WARNING — gentle zoom-in/out beam OUTSIDE the orb only.
          Uses the pillar's palette (or amber fallback) so the master
          ball stays in its current colour and just breathes a halo,
          rather than morphing internally. */}
      {warning && (
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          animate={{ scale: [1.05, 1.45, 1.05], opacity: [0.0, 0.85, 0.0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background: `radial-gradient(circle, ${warningPalette ? warningPalette.halo : 'rgba(255,170,60,0.7)'} 0%, transparent 68%)`,
            filter: 'blur(16px)',
          }}
        />
      )}
      {/* OUTER ring — silver chrome by default; transmutes into a
          radiant gold beam when the user reaches Divine (5/5 fused). */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{ rotate: spinDur ? 360 : 0 }}
        transition={{ duration: spinDur || (tier === 'divine' ? 14 : 0.001), repeat: Infinity, ease: 'linear' }}
        style={{
          background:
            tier === 'divine'
              ? 'conic-gradient(from 90deg, #FFF6D0 0%, #FFD66A 12%, #F5C24A 25%, #B8842A 38%, ' +
                '#FFEFA8 50%, #FFD66A 63%, #F5C24A 75%, #B8842A 88%, #FFF6D0 100%)'
              : 'conic-gradient(from 90deg, #f6f6f8 0%, #8b8d94 12%, #c3c5cc 25%, #4a4c52 38%, ' +
                '#e8e9ec 50%, #8b8d94 63%, #c3c5cc 75%, #4a4c52 88%, #f6f6f8 100%)',
          boxShadow:
            tier === 'divine'
              ? `0 0 0 1px rgba(90,55,10,0.6) inset, 0 0 38px rgba(255,214,106,0.9), 0 0 80px rgba(255,235,150,0.55)`
              : `0 0 0 1px rgba(0,0,0,0.6) inset, 0 0 28px ${palette.halo}, 0 0 60px rgba(0,0,0,0)`,
          padding: 6,
          transition: 'background 0.6s ease, box-shadow 0.6s ease',
        }}>
        <div className="w-full h-full rounded-full" style={{ background: '#04050A' }} />
      </motion.div>

      {/* SECOND ring (counter-rotating) — also flips to gold on Divine. */}
      <motion.div
        className="absolute rounded-full"
        animate={{ rotate: spinDur ? -360 : 0 }}
        transition={{ duration: (spinDur || (tier === 'divine' ? 14 : 0.001)) * 1.6, repeat: Infinity, ease: 'linear' }}
        style={{
          inset: '6.5%',
          background:
            tier === 'divine'
              ? 'conic-gradient(from 0deg, #FFEFA8 0%, #B8842A 25%, #FFF6D0 50%, #B8842A 75%, #FFEFA8 100%)'
              : 'conic-gradient(from 0deg, #d8dade 0%, #5a5c62 25%, #f0f1f4 50%, #5a5c62 75%, #d8dade 100%)',
          padding: 2,
          boxShadow:
            tier === 'divine'
              ? 'inset 0 0 0 1px rgba(90,55,10,0.6), 0 0 22px rgba(255,214,106,0.7)'
              : 'inset 0 0 0 1px rgba(0,0,0,0.55)',
          transition: 'background 0.6s ease, box-shadow 0.6s ease',
        }}>
        <div className="w-full h-full rounded-full" style={{ background: '#04050A' }} />
      </motion.div>

      {/* Divine-only: a slow, breathing gold beam halo surrounding the
          outer ring — gives the "shining beam" effect the user asked for. */}
      {tier === 'divine' && (
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          animate={{ opacity: [0.55, 1, 0.55], scale: [1.02, 1.12, 1.02] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background:
              'conic-gradient(from 0deg, rgba(255,246,208,0) 0%, rgba(255,214,106,0.55) 20%, rgba(255,246,208,0.9) 50%, rgba(255,214,106,0.55) 80%, rgba(255,246,208,0) 100%)',
            filter: 'blur(10px)',
            mixBlendMode: 'screen',
          }}
        />
      )}

      {/* THIRD ring — thin glowing accent */}
      <div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: '11%',
          border: `1px solid ${palette.core}`,
          boxShadow:
            tier === 'baseline' && !beamAlways
              ? `inset 0 0 12px ${palette.halo}, 0 0 8px ${palette.halo}`
              : `inset 0 0 22px ${palette.halo}, 0 0 18px ${palette.halo}`,
          opacity: 0.85,
          transition: 'box-shadow 0.6s ease',
        }}
      />

      {/* PLASMA SPHERE */}
      <div
        className="absolute rounded-full overflow-hidden"
        style={{
          inset: '14%',
          background: `radial-gradient(circle at 35% 30%, ${palette.core} 0%, ${palette.mid} 35%, ${palette.outer} 75%, #050214 100%)`,
          boxShadow:
            `inset 0 0 36px ${palette.halo}, inset 0 0 10px rgba(0,0,0,0.7), 0 0 24px ${palette.halo}`,
          transition: 'background 0.6s ease, box-shadow 0.6s ease',
        }}>
        <motion.div
          aria-hidden
          className="absolute inset-0"
          animate={{ rotate: 360, scale: [1, 1.08, 1] }}
          transition={{ rotate: { duration: 18, repeat: Infinity, ease: 'linear' },
                        scale:  { duration: 4.5, repeat: Infinity, ease: 'easeInOut' } }}
          style={{
            background:
              `radial-gradient(ellipse at 60% 40%, ${palette.core}66 0%, transparent 45%),` +
              `radial-gradient(ellipse at 30% 70%, ${palette.mid}88 0%, transparent 50%)`,
            mixBlendMode: 'screen',
            opacity: 0.85,
          }}
        />
        {(tier === 'resonance' || tier === 'awakened') && (
          <motion.svg
            viewBox="0 0 100 100"
            className="absolute inset-0 w-full h-full"
            animate={{ opacity: [0.4, 0.95, 0.4] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            style={{ mixBlendMode: 'screen' }}>
            <path
              d="M50 18 L 44 42 L 56 44 L 38 78 L 50 56 L 40 54 L 56 26 Z"
              fill={palette.core}
              opacity="0.85"
              style={{ filter: `drop-shadow(0 0 5px ${palette.accent || palette.core})` }}
            />
          </motion.svg>
        )}
        {tier === 'divine' && (
          <motion.div
            aria-hidden
            className="absolute inset-0"
            animate={{ opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              background:
                'radial-gradient(circle at 50% 50%, #FFFFFF 0%, #FFF1B8 18%, #F1D27A 38%, transparent 70%)',
              mixBlendMode: 'screen',
            }}
          />
        )}
        <div
          aria-hidden
          className="absolute"
          style={{
            top: '8%', left: '14%', width: '40%', height: '28%',
            background: 'radial-gradient(ellipse, rgba(255,255,255,0.55) 0%, transparent 70%)',
            filter: 'blur(2px)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {!paletteOverride && (
        <div className="absolute -bottom-4 left-0 right-0 text-center pointer-events-none">
          <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.3em]"
            style={{ color: tier === 'divine' ? '#FFE090' : tier === 'resonance' ? '#9CD8FF' : tier === 'awakened' ? '#C8A1FF' : 'rgba(255,255,255,0.4)' }}>
            {tier} · {Math.round(volume)}%
          </p>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PILLAR NODE — small hollow chrome ring on the Timeline of Ascension.
   Press-and-hold 1.5 s to TOGGLE fuse: a first hold ignites the node,
   a second hold unfuses it. When `enabled` is false (current time is
   outside the pillar's window) the node renders dim and the hold
   gesture is blocked.
   ───────────────────────────────────────────────────────────────── */
const PillarNodeImpl = ({ pillar, fused, enabled = true, windowLabel, warning = false, alarming = false, onToggleFuse, onLabelTap }) => {
  const [progress, setProgress] = useState(0);
  const holdRef = useRef(null);
  const tickRef = useRef(null);
  const rumbleRef = useRef(null);
  const HOLD_MS = 1500;

  const startHold = useCallback((e) => {
    if (!enabled) return;
    e.preventDefault();
    nativeVibrate(12);
    const startedAt = performance.now();
    tickRef.current = window.setInterval(() => {
      const elapsed = performance.now() - startedAt;
      setProgress(Math.min(1, elapsed / HOLD_MS));
    }, 30);
    let beat = 0;
    rumbleRef.current = window.setInterval(() => {
      beat += 1;
      const dur = 8 + beat * 5;
      nativeVibrate(dur);
    }, 250);
    holdRef.current = window.setTimeout(() => {
      if (rumbleRef.current) { clearInterval(rumbleRef.current); rumbleRef.current = null; }
      nativeVibrate([55, 35, 90, 30, 60]);
      onToggleFuse(pillar.id);
      setProgress(0);
    }, HOLD_MS);
  }, [enabled, onToggleFuse, pillar.id]);

  const cancelHold = useCallback(() => {
    if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (rumbleRef.current) { clearInterval(rumbleRef.current); rumbleRef.current = null; }
    setProgress(0);
  }, []);

  useEffect(() => () => { cancelHold(); }, [cancelHold]);

  const NODE_SIZE = 36;
  /* Four visual states:
       1. enabled + !fused  → hollow chrome ring, "Hold to fuse"
       2. enabled + fused   → ignited cyan core, "Hold to unfuse"
       3. !enabled + fused  → SEALED FUSED: still glowing cyan, locked,
                              caption "Sealed · HH:MM – HH:MM"
       4. !enabled + !fused → DIM + grayscale, "Locked · HH:MM – HH:MM"
       The DIM filter is only applied to state 4 so a sealed-fused
       node stays bright after its window closes. */
  const isLockedFused = !enabled && fused;
  const isLockedDim = !enabled && !fused;

  const accent = isLockedFused
    ? '#5DD4FF'
    : isLockedDim
      ? 'rgba(180,200,220,0.18)'
      : fused ? '#5DD4FF' : 'rgba(180,200,220,0.45)';
  const glow = fused ? 'rgba(93,212,255,0.85)' : 'rgba(180,200,220,0.12)';

  const statusLine = isLockedFused
    ? (windowLabel ? `Sealed · ${windowLabel}` : 'Sealed')
    : isLockedDim
      ? (windowLabel ? `Locked · ${windowLabel}` : 'Locked')
      : (fused ? `Fused via ${pillar.sourceTile || pillar.label}` : `Auto-fuses via ${pillar.sourceTile || pillar.label}`);

  return (
    <div
      className="relative flex items-center"
      style={{
        height: NODE_SIZE,
        opacity: isLockedDim ? 0.55 : 1,
        transition: 'opacity 0.4s ease',
      }}>
      <button
        type="button"
        onClick={() => onLabelTap(pillar)}
        className="absolute right-full mr-3 text-right whitespace-nowrap"
        data-testid={`pillar-label-${pillar.id}`}>
        <p className="font-['Cormorant_Garamond'] italic"
          style={{ color: fused ? '#CFE9FF' : 'rgba(255,255,255,0.85)', fontSize: 14 }}>
          {pillar.label}
        </p>
        <p className="font-['Outfit'] text-[8px] uppercase tracking-[0.18em]"
          style={{
            color: isLockedFused ? '#5DD4FF' : isLockedDim ? 'rgba(255,255,255,0.4)' : fused ? '#5DD4FF' : 'rgba(255,255,255,0.5)',
          }}>
          {statusLine}
        </p>
      </button>

      {/* (No per-node halo — the warning lives on the master orb and
          the pillar card; the timeline ball itself stays clean to
          avoid paint artifacts behind the central wire.) */}

      <motion.button
        type="button"
        onClick={() => onLabelTap(pillar)}
        animate={alarming ? { opacity: [1, 0.5, 1], scale: [1, 1.15, 1] } : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, repeat: alarming ? Infinity : 0, ease: 'easeInOut' }}
        className="relative rounded-full select-none touch-none disabled:cursor-not-allowed"
        style={{
          width: NODE_SIZE, height: NODE_SIZE,
          background: alarming ? pillar.palette.mid : '#04050A',
          border: `2px solid ${alarming ? pillar.palette.accent : accent}`,
          boxShadow: alarming
            ? `inset 0 0 18px ${pillar.palette.halo}, 0 0 30px ${pillar.palette.halo}, 0 0 70px ${pillar.palette.halo}`
            : (fused)
              ? `inset 0 0 14px ${glow}, 0 0 24px ${glow}, 0 0 60px ${glow}`
              : `inset 0 0 6px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)`,
          transition: alarming ? 'none' : 'box-shadow 0.4s ease, border-color 0.4s ease',
          filter: (isLockedDim && !alarming) ? 'grayscale(0.6) brightness(0.7)' : 'none',
          willChange: alarming ? 'opacity, transform, box-shadow' : 'auto',
        }}
        data-testid={`pillar-node-${pillar.id}`}
        data-alarming={alarming ? 'true' : 'false'}
        aria-label={`Open ${pillar.label} details`}
        aria-pressed={fused}>
        {enabled && progress > 0 && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              border: `2px solid ${fused ? '#FFAA66' : '#5DD4FF'}`,
              transform: `scale(${1 - progress * 0.55})`,
              opacity: 0.95,
              boxShadow: `0 0 ${12 + progress * 18}px ${fused ? 'rgba(255,170,102,' : 'rgba(93,212,255,'}${0.4 + progress * 0.55})`,
              transition: 'transform 30ms linear, box-shadow 30ms linear',
            }}
          />
        )}
        {fused && (
          <motion.span
            aria-hidden
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            className="absolute rounded-full"
            style={{
              inset: '28%',
              background: 'radial-gradient(circle, #FFFFFF 0%, #5DD4FF 45%, transparent 80%)',
              boxShadow: '0 0 14px rgba(93,212,255,0.9)',
            }}
          />
        )}
      </motion.button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PILLAR CARD — replaces the old Discover Feed.
   A premium chrome-rim aura ball rendered in the pillar's colour,
   with name + role caption below. Tap → opens that pillar's Energy
   Box. (Hold-to-fuse still lives on the timeline node above.)
   ───────────────────────────────────────────────────────────────── */
const PillarCardImpl = ({ pillar, fused, taskCount, warning = false, alarming = false, onClick }) => {
  const dormantPalette = {
    ...pillar.palette,
    core: pillar.palette.mid,
    mid: pillar.palette.outer,
    halo: pillar.palette.halo.replace(/0?\.\d+\)$/, '0.25)'),
  };
  const isPulsing = alarming || warning;
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: 1.03 }}
      onClick={() => onClick(pillar.id)}
      animate={alarming ? { opacity: [1, 0.55, 1], scale: [1, 1.035, 1] } : { opacity: 1, scale: 1 }}
      transition={{ duration: 0.9, repeat: alarming ? Infinity : 0, ease: 'easeInOut' }}
      className="relative rounded-2xl overflow-hidden flex items-center justify-center"
      style={{
        aspectRatio: '1.5 / 1',    /* shorter cards — hug the ball, free vertical space */
        padding: 3,
        background:
          `linear-gradient(160deg, ${pillar.palette.mid}24 0%, rgba(0,0,0,0.55) 70%)`,
        border: `1px solid ${alarming ? pillar.palette.accent : fused ? pillar.palette.accent : 'rgba(255,255,255,0.06)'}`,
        boxShadow: alarming
          ? `0 0 28px ${pillar.palette.halo}, inset 0 0 22px ${pillar.palette.halo}`
          : fused
          ? `0 0 18px ${pillar.palette.halo}, inset 0 0 18px ${pillar.palette.halo}`
          : `0 3px 12px rgba(0,0,0,0.5), inset 0 0 10px rgba(255,255,255,0.02)`,
        willChange: isPulsing ? 'opacity, transform' : 'auto',
      }}
      data-testid={`pillar-card-${pillar.id}`}
      data-alarming={alarming ? 'true' : 'false'}
      aria-label={`Open ${pillar.label}`}>
      {/* (Outer card warning ring removed — only the inner BALL
          breathes in/out, the card frame stays static.) */}

      {/* Ball with BOTH texts (Label + role) stacked INSIDE the orb. */}
      <div className="relative flex items-center justify-center" style={{ width: 44, height: 44 }}>
        <motion.div
          animate={warning ? {
            filter: ['brightness(0.85)', 'brightness(1.3)', 'brightness(0.85)'],
            scale: [1, 1.05, 1],
          } : { filter: 'brightness(1)', scale: 1 }}
          transition={{ duration: 2.4, repeat: warning ? Infinity : 0, ease: 'easeInOut' }}>
          <AuraArtifact
            tier={fused ? 'resonance' : 'awakened'}
            palette={fused ? pillar.palette : dormantPalette}
            beamAlways={true}
            size={44}
            warning={false}
          />
        </motion.div>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-0.5">
          <p
            className="font-['Cormorant_Garamond'] italic font-medium leading-none"
            style={{
              color: '#FFFFFF',
              fontSize: 8,
              letterSpacing: '0.02em',
              textShadow: `0 1px 2px rgba(0,0,0,0.95), 0 0 8px ${pillar.palette.halo}`,
            }}>
            {pillar.label}
          </p>
          <p
            className="font-['Outfit'] uppercase mt-0.5 leading-none text-center"
            style={{
              color: '#FFFFFF',
              fontSize: 4.5,
              letterSpacing: '0.14em',
              textShadow: `0 1px 2px rgba(0,0,0,0.95)`,
              opacity: 0.85,
            }}>
            {pillar.role}
          </p>
        </div>
      </div>

      <div className="absolute top-1 right-1 flex items-center gap-1">
        {fused && (
          <span
            className="w-3 h-3 rounded-full flex items-center justify-center"
            style={{
              background: pillar.palette.accent,
              boxShadow: `0 0 8px ${pillar.palette.halo}`,
            }}>
            <Flame size={7} color="#000" strokeWidth={3} />
          </span>
        )}
      </div>
    </motion.button>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ENERGY BOX — full-page pillar detail panel with intent list
   ───────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────
   Memoised wrappers — these components do expensive paint work
   (motion springs, conic gradients, box-shadow blurs). The dashboard
   re-renders every 1 s during the 60-s countdown; without these
   wrappers each tick repaints the timeline balls and leaves
   horizontal "crash line" artifacts behind the central wire.

   Comparators ignore identity-changes in callback props (which the
   dashboard recreates on every render).
   ───────────────────────────────────────────────────────────────── */

const AuraArtifact = memo(AuraArtifactImpl, (prev, next) => (
  prev.tier === next.tier &&
  prev.volume === next.volume &&
  prev.size === next.size &&
  prev.beamAlways === next.beamAlways &&
  prev.warning === next.warning &&
  prev.palette === next.palette
));

const PillarNode = memo(PillarNodeImpl, (prev, next) => (
  prev.pillar === next.pillar &&
  prev.fused === next.fused &&
  prev.enabled === next.enabled &&
  prev.warning === next.warning &&
  prev.alarming === next.alarming &&
  prev.windowLabel === next.windowLabel
  /* onToggleFuse / onLabelTap intentionally excluded — they're
     recreated each render but their behaviour is stable. */
));

const PillarCard = memo(PillarCardImpl, (prev, next) => (
  prev.pillar === next.pillar &&
  prev.fused === next.fused &&
  prev.taskCount === next.taskCount &&
  prev.warning === next.warning &&
  prev.alarming === next.alarming
));

const EnergyBox = ({ pillar, state, onUpdate, onClose }) => {
  const fused = !!state.fused;
  // Migrate old { window: {start, end} } → { alarms: [start] }. Keep alarms
  // as an array of HH:MM strings, max 5. A "5-min fuse window" is implied
  // per alarm — no end time needed.
  const migratedAlarms = (() => {
    if (Array.isArray(state.alarms)) return state.alarms.slice(0, 5);
    if (state.window?.start) return [state.window.start];
    return [];
  })();
  const alarms = migratedAlarms;

  // alarmNoteIds[i] links alarm i to an Energy-tile worship note (energy pillar only).
  const ids = Array.isArray(state.alarmNoteIds) ? state.alarmNoteIds : [];
  const setAlarmAt = (idx, hhmm) => {
    const len = Math.max(alarms.length, idx + 1);
    const pairs = Array.from({ length: len }, (_, i) => [i === idx ? hhmm : (alarms[i] || ''), ids[i] || null]);
    const kept = pairs.filter(([t]) => Boolean(t)).slice(0, 5);
    onUpdate({ ...state, alarms: kept.map(([t]) => t), alarmNoteIds: kept.map(([, id]) => id) });
  };
  const removeAlarmAt = (idx) => {
    onUpdate({
      ...state,
      alarms: alarms.filter((_, i) => i !== idx),
      alarmNoteIds: alarms.map((_, i) => ids[i] || null).filter((_, i) => i !== idx),
    });
  };
  const addAlarm = () => {
    if (alarms.length >= 5) return;
    onUpdate({ ...state, alarms: [...alarms, ''], alarmNoteIds: [...alarms.map((_, i) => ids[i] || null), null] });
  };

  return (
    <motion.div
      key={pillar.id}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
      className="fixed inset-0 z-[160] flex flex-col overflow-hidden imperium-shell"
      style={{
        backgroundColor: '#000',                          // solid base so nothing bleeds through
        backgroundImage:
          `radial-gradient(ellipse at top, ${pillar.palette.mid}33 0%, transparent 55%),` +
          `radial-gradient(ellipse at 50% 110%, ${pillar.palette.halo} 0%, transparent 55%)`,
      }}
      data-testid={`energy-box-${pillar.id}`}>

      <div
        className="relative flex items-center px-4 py-3 flex-shrink-0"
        style={{ paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)' }}>
        <BackPill onClick={onClose} accent="#5DD4FF" testId={`energy-box-back-${pillar.id}`}/>
        {/* Absolutely-centered title so back button never nudges it */}
        <div className="absolute left-0 right-0 flex flex-col items-center pointer-events-none">
          <h2 className="font-['Cormorant_Garamond'] italic text-lg leading-none"
            style={{ color: '#FFFFFF', letterSpacing: '0.18em',
              textShadow: `0 0 14px ${pillar.palette.halo}` }}>
            {pillar.label}
          </h2>
          <p className="font-['Outfit'] uppercase tracking-[0.22em] mt-1"
            style={{ color: pillar.palette.accent, fontSize: 9 }}>
            {pillar.role}
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto relative">
        <div className="min-h-full flex flex-col justify-center py-2">
        <div className="flex flex-col items-center pt-2 pb-1">
          {(() => {
            // If THIS pillar is the currently-alarming one, the orb becomes
            // the STOP button and displays "{Pillar} alarming" in gold.
            const activeId = (() => {
              try { return localStorage.getItem('codex_alarm_active') || null; }
              catch (_) { return null; }
            })();
            const isAlarming = activeId === pillar.id;
            const orbSize = 124;             /* matches master orb size */
            if (isAlarming) {
              return (
                <button type="button"
                  onClick={() => {
                    try { stopSignatureTone(); } catch (_) { /* ignore */ }
                    try { localStorage.removeItem('codex_alarm_active'); } catch (_) { /* ignore */ }
                    try { localStorage.removeItem('codex_alarm_started_at'); } catch (_) { /* ignore */ }
                    try {
                      window.dispatchEvent(new CustomEvent('codex-alarm', {
                        detail: { pillarId: null, phase: 'stop' },
                      }));
                    } catch (_) { /* ignore */ }
                  }}
                  className="relative flex items-center justify-center"
                  style={{ width: orbSize, height: orbSize }}
                  data-testid={`energy-stop-${pillar.id}`}
                  aria-label={`Stop ${pillar.label} alarm`}>
                  <AuraArtifact
                    tier="resonance"
                    palette={pillar.palette}
                    beamAlways={true}
                    size={orbSize}
                  />
                  {/* White overlay text — dead-centered on the orb, bright against any palette */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="font-['Cinzel'] uppercase text-[12px] leading-none"
                      style={{
                        color: '#FFFFFF',
                        letterSpacing: '0.24em',
                        paddingLeft: '0.24em',   /* offset trailing letter-spacing */
                        textShadow: '0 0 10px rgba(0,0,0,0.9), 0 0 3px rgba(0,0,0,0.85)',
                      }}>
                      {pillar.label}
                    </span>
                    <span className="font-['Outfit'] italic text-[9px] mt-1 leading-none"
                      style={{
                        color: '#FFFFFF',
                        textShadow: '0 0 8px rgba(0,0,0,0.85)',
                      }}>
                      alarming · tap
                    </span>
                  </div>
                </button>
              );
            }
            return (
              <AuraArtifact
                tier={fused ? 'resonance' : 'awakened'}
                palette={pillar.palette}
                beamAlways={true}
                size={orbSize}
              />
            );
          })()}
        </div>

        <div className="px-6 mb-2 text-center">
          <p className="font-['Cormorant_Garamond'] italic"
            style={{
              color: '#FFFFFF',
              fontSize: 'clamp(12px, 3.4vw, 14px)',
              lineHeight: 1.3,
              textShadow: `0 0 18px ${pillar.palette.halo}`,
            }}>
            &ldquo;{pillar.passage}&rdquo;
          </p>
          <div className="mx-auto mt-1.5 w-16 h-[1px]"
            style={{ background: `linear-gradient(90deg, transparent, ${pillar.palette.accent}, transparent)` }} />
        </div>

        <div className="px-5 pb-3">
          {/* 5 vertical alarm slots — one HH:MM per slot. 5-minute fuse
              window is implicit. Empty slots show a "Set time" placeholder
              and become a real alarm once the user picks a time. */}
          <div className="flex items-center justify-between mb-2">
            <p className="font-['Outfit'] uppercase tracking-[0.24em] text-[11px]"
              style={{ color: 'rgba(255,255,255,0.55)' }}>
              Alarms · {alarms.filter(Boolean).length}/5
            </p>
            <p className="font-['Outfit'] uppercase tracking-[0.20em] text-[10px]"
              style={{ color: `${pillar.palette.accent}AA` }}>
              1-min fuse alarm
            </p>
          </div>

          <div className="space-y-1.5">
            {Array.from({ length: 5 }).map((_, idx) => {
              const hhmm = alarms[idx] || '';
              const isSet = !!hhmm;
              return (
                <div
                  key={idx}
                  className="flex items-center gap-2 rounded-xl px-3 py-1.5"
                  style={{
                    background: isSet
                      ? `linear-gradient(160deg, ${pillar.palette.mid}33 0%, rgba(0,0,0,0.55) 100%)`
                      : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${isSet ? `${pillar.palette.accent}88` : 'rgba(255,255,255,0.10)'}`,
                    boxShadow: isSet
                      ? `0 0 10px ${pillar.palette.halo}, inset 0 0 6px ${pillar.palette.accent}22`
                      : 'none',
                  }}
                  data-testid={`energy-alarm-slot-${pillar.id}-${idx}`}>
                  <span className="font-['Outfit'] uppercase tracking-[0.22em] text-[10px] w-8"
                    style={{ color: isSet ? pillar.palette.accent : 'rgba(255,255,255,0.35)' }}>
                    #{idx + 1}
                  </span>
                  <label className="relative flex-1 flex items-center justify-center cursor-pointer">
                    <input
                      type="time"
                      id={`aura-alarm-${pillar.id}-${idx}`}
                      onClick={(e) => openPicker(e.currentTarget)}
                      value={hhmm}
                      onChange={(e) => setAlarmAt(idx, e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      style={{ colorScheme: 'dark' }}
                      data-testid={`energy-alarm-input-${pillar.id}-${idx}`}
                    />
                    <span className="font-['Cormorant_Garamond'] italic text-sm pointer-events-none"
                      style={{ color: isSet ? '#FFFFFF' : 'rgba(255,255,255,0.28)' }}>
                      {isSet ? formatTime12(hhmm) : 'Set time'}
                    </span>
                  </label>
                  <ChevronDown
                    size={13}
                    onClick={() => openPicker(document.getElementById(`aura-alarm-${pillar.id}-${idx}`))}
                    className="cursor-pointer"
                    style={{ color: isSet ? `${pillar.palette.accent}CC` : 'rgba(255,255,255,0.30)' }}
                  />
                  {isSet ? (
                    <button
                      type="button"
                      onClick={() => removeAlarmAt(idx)}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white/80"
                      aria-label="Clear alarm"
                      data-testid={`energy-alarm-remove-${pillar.id}-${idx}`}>
                      <Trash2 size={12} />
                    </button>
                  ) : (
                    <button type="button"
                      onClick={() => openPicker(document.getElementById(`aura-alarm-${pillar.id}-${idx}`))}
                      className="w-6 h-6 flex items-center justify-center rounded-full active:scale-90 transition-transform"
                      style={{
                        border: `1px solid ${pillar.palette.accent}55`,
                        color: `${pillar.palette.accent}AA`,
                      }}
                      aria-label="Set alarm time"
                      data-testid={`energy-alarm-add-${pillar.id}-${idx}`}>
                      <Plus size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>
      </div>

      <div
        className="relative flex-shrink-0 px-5 pt-3"
        style={{
          boxSizing: 'border-box',
          borderTop: `1px solid ${pillar.palette.accent}22`,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.0), rgba(0,0,0,0.85))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 14px)',
        }}>
        {/* Single informational box — no fuse action, no input. User fuses
             in the respective Bliss Tile once an alarm fires. */}
        <div
          className="w-full rounded-2xl px-4 py-3 text-center"
          style={{
            background: `linear-gradient(180deg, ${pillar.palette.mid}33 0%, rgba(0,0,0,0.55) 100%)`,
            border: `1px solid ${pillar.palette.accent}88`,
            boxShadow: `0 0 22px ${pillar.palette.halo}, inset 0 0 14px ${pillar.palette.accent}22`,
          }}
          data-testid={`energy-info-${pillar.id}`}>
          <p className="font-['Cinzel'] uppercase tracking-[0.28em] text-[12px]"
            style={{
              color: pillar.palette.accent,
              textShadow: `0 0 12px ${pillar.palette.halo}`,
            }}>
            Initiate {pillar.label}
          </p>
          <p className="font-['Cormorant_Garamond'] italic mt-1.5 leading-snug"
            style={{ color: '#FFFFFF', fontSize: 13 }}>
            {pillar.id === 'power'
              ? 'Begin the reps · '
              : pillar.id === 'fuel'
              ? 'Begin the nutrition · '
              : pillar.id === 'force'
              ? 'Begin the yoga · '
              : pillar.id === 'energy'
              ? 'Begin the breath · '
              : pillar.id === 'destiny'
              ? 'Begin the manifestation · '
              : 'Begin the regimen · '}
            <span style={{ color: pillar.palette.accent, fontWeight: 500 }}>
              Fuse it in the {pillar.label} tile (anytime today)
            </span>
          </p>
        </div>
      </div>
    </motion.div>
  );
};


/* ─────────────────────────────────────────────────────────────────
   AURA DASHBOARD — main entry
   ───────────────────────────────────────────────────────────────── */
export const AuraDashboard = ({ onClose }) => {
  const [state, setState] = useState(loadState);
  const [surge, setSurge] = useState(null);
  const [openPillar, setOpenPillar] = useState(null);
  /* Currently-ringing alarm pillar id (drives blink across AuraCodex, its
     inner ball, and the underlying Bliss Tile). Null when idle. */
  const [alarmPillarId, setAlarmPillarId] = useState(() => {
    try { return localStorage.getItem('codex_alarm_active') || null; } catch (_) { return null; }
  });
  /* Current SECOND-of-day; the ticker speed adapts: 1 s when any
     pillar is within 90 s of its window-end-while-unfused, otherwise
     30 s for cheap window-gating. */
  const [nowSecs, setNowSecs] = useState(() => {
    const d = new Date(); return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  });

  /* Pre-compute per-pillar gating + warning state. */
  const pillarStatus = PILLARS.map((p) => {
    const ps = state.pillars[p.id] || {};
    const { inside, untilEnd } = windowStatus(ps.window, nowSecs);
    return {
      pillar: p,
      ps,
      enabled: inside,
      lockedFused: !inside && !!ps.fused,
      lockedDim:   !inside && !ps.fused,
      /* WARNING — inside the window, not yet fused, and last 60 seconds. */
      warning: inside && !ps.fused && untilEnd > 0 && untilEnd <= 60,
      secondsLeft: Math.max(0, Math.floor(untilEnd)),
    };
  });
  /* Pick the closest-to-expiring warning pillar (drives master pulse). */
  const masterWarning = pillarStatus
    .filter((x) => x.warning)
    .sort((a, b) => a.secondsLeft - b.secondsLeft)[0] || null;

  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => { saveState(state); }, [state]);

  /* ═══════════ AUTO-FUSE from source tiles ═══════════
     Reads today's ping signals from the shared `aura_source_pings_v1`
     key (written by each source tile when the user completes today's
     ritual) plus legacy localStorage schemas as a fallback. Polls
     every 2s. Never un-fuses — once fused for the day, stays fused. */
  useEffect(() => {
    const detect = () => {
      const today = todayKey();
      const detected = {};

      /* Primary — shared ping bus */
      try {
        const raw = localStorage.getItem('aura_source_pings_v1');
        if (raw) {
          const s = JSON.parse(raw);
          Object.entries(s || {}).forEach(([pid, dateStr]) => {
            if (dateStr === today) detected[pid] = true;
          });
        }
      } catch { /* noop */ }

      /* Fallback — Fuel `fuel_fuse_v1.fusedDays[today]` */
      try {
        const raw = localStorage.getItem('fuel_fuse_v1');
        if (raw) {
          const s = JSON.parse(raw);
          if (s?.fusedDays?.[today]) detected.fuel = true;
        }
      } catch { /* noop */ }

      /* Fallback — Aegis: any category with today-indexed fusedDay */
      try {
        const raw = localStorage.getItem('aegis_regimen_v1');
        if (raw) {
          const s = JSON.parse(raw);
          const anyCat = Object.values(s || {}).some((cat) => {
            const forge = cat?.forge;
            if (!forge?.startDate) return false;
            const start = new Date(forge.startDate);
            const todayD = new Date(today);
            const diff = Math.floor((todayD - start) / 86400000) + 1;
            return diff > 0 && !!forge.fusedDays?.[diff];
          });
          if (anyCat) detected.aegis = true;
        }
      } catch { /* noop */ }

      setState((cur) => {
        let changed = false;
        const next = { ...cur.pillars };
        Object.entries(detected).forEach(([pid, isFused]) => {
          if (isFused && !cur.pillars[pid]?.fused) {
            next[pid] = { ...(cur.pillars[pid] || {}), fused: true, lastFusedAt: Date.now() };
            changed = true;
          }
        });
        return changed ? { ...cur, pillars: next } : cur;
      });
    };
    detect();
    const t = window.setInterval(detect, 2000);
    return () => clearInterval(t);
  }, []);

  /* Adaptive ticker — 1 s during any 90-s pre-end window, 30 s otherwise. */
  useEffect(() => {
    const anyClose = PILLARS.some((p) => {
      const ps = state.pillars[p.id];
      if (!ps || ps.fused) return false;
      const { inside, untilEnd } = windowStatus(ps.window, nowSecs);
      return inside && untilEnd <= 90;
    });
    const interval = anyClose ? 1000 : 30 * 1000;
    const t = window.setInterval(() => {
      if (state.date !== todayKey()) setState((cur) => freshDay(cur));
      const d = new Date();
      setNowSecs(d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds());
    }, interval);
    return () => clearInterval(t);
  }, [state, nowSecs]);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    // Ask for browser Notification permission on first Codex open —
    // required for the "Bliss AuraCodex · {Pillar} Time" popup to
    // appear when an alarm fires in preview / web builds.
    try {
      if (typeof window !== 'undefined' && 'Notification' in window
          && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => { /* ignore */ });
      }
    } catch (_) { /* ignore */ }
    return () => { document.body.style.overflow = ''; };
  }, []);

  /* Alarms are fired by the app-root CodexAlarmWatcher; mirror its state here. */
  useEffect(() => {
    const onAlarm = (e) => setAlarmPillarId(e.detail?.pillarId || null);
    window.addEventListener('codex-alarm', onAlarm);
    return () => window.removeEventListener('codex-alarm', onAlarm);
  }, []);

  /* Energy tile ↔ Codex energy alarms — pull worship times on open and
     whenever the Energy tile pushes an update. */
  useEffect(() => {
    const applyEnergy = (e) => {
      const { alarms, alarmNoteIds } = e.detail || {};
      setState((cur) => ({
        ...cur,
        pillars: { ...cur.pillars, energy: { ...cur.pillars.energy, alarms, alarmNoteIds } },
      }));
    };
    window.addEventListener('codex-energy-sync', applyEnergy);
    pullEnergyNotesIntoCodex();
    return () => window.removeEventListener('codex-energy-sync', applyEnergy);
  }, []);

  const fusedCount = PILLARS.reduce((n, p) => n + (state.pillars[p.id]?.fused ? 1 : 0), 0);
  const { tier, volume } = computeTier(fusedCount);

  /* Master orb adopts the MOST RECENTLY FUSED pillar's palette so the
     core glows in the colour of that pillar's card (ruby for Vitality,
     emerald for Essence, etc.). At baseline (none fused) we fall back
     to the tier-based palette (dormant violet). */
  const lastFused = PILLARS
    .map((p) => ({ p, ts: state.pillars[p.id]?.lastFusedAt || 0 }))
    .filter((x) => x.ts > 0 && !!state.pillars[x.p.id]?.fused)
    .sort((a, b) => b.ts - a.ts)[0];
  const masterPalette = lastFused ? lastFused.p.palette : undefined;

  const handleToggleFuse = (pillarId) => {
    setState((cur) => {
      const wasFused = !!cur.pillars[pillarId]?.fused;
      const nextFused = !wasFused;
      return {
        ...cur,
        pillars: {
          ...cur.pillars,
          [pillarId]: {
            ...cur.pillars[pillarId],
            fused: nextFused,
            /* Record fuse timestamp so the master orb adopts this
               pillar's colour. On unfuse, zero it out. */
            lastFusedAt: nextFused ? Date.now() : 0,
          },
        },
      };
    });
    const wasFused = !!state.pillars[pillarId]?.fused;
    if (!wasFused) {
      const idx = PILLARS.findIndex((p) => p.id === pillarId);
      setSurge({ key: `${pillarId}-${Date.now()}`, idx });
      window.setTimeout(() => setSurge(null), 900);
    }
  };

  const updatePillar = useCallback((pillarId, next) => {
    const prev = stateRef.current.pillars[pillarId];
    setState((cur) => ({ ...cur, pillars: { ...cur.pillars, [pillarId]: next } }));
    if (pillarId === 'energy') {
      scheduleCodexEnergyPush(prev, next, (ids) => {
        setState((cur) => ({
          ...cur,
          pillars: { ...cur.pillars, energy: { ...cur.pillars.energy, alarmNoteIds: ids } },
        }));
      });
    }
  }, []);

  const openPillarData = openPillar ? PILLARS.find((p) => p.id === openPillar) : null;
  const openPillarState = openPillar ? state.pillars[openPillar] : null;

  return (
    <div
      className="fixed inset-0 z-[150] flex flex-col overflow-hidden imperium-shell"
      style={{ background: '#04050A' }}
      data-testid="aura-dashboard"
    >
      {/* star-field */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background:
          'radial-gradient(circle at 10% 20%, rgba(255,255,255,0.04) 0%, transparent 0.4%),' +
          'radial-gradient(circle at 80% 12%, rgba(255,255,255,0.05) 0%, transparent 0.3%),' +
          'radial-gradient(circle at 25% 78%, rgba(255,255,255,0.04) 0%, transparent 0.3%),' +
          'radial-gradient(circle at 92% 60%, rgba(255,255,255,0.05) 0%, transparent 0.3%),' +
          'radial-gradient(circle at 55% 35%, rgba(255,255,255,0.03) 0%, transparent 0.25%),' +
          'radial-gradient(ellipse at top, rgba(28,18,80,0.45) 0%, transparent 60%)',
      }} />

      {/* Top bar — pinned to absolute top of dashboard, minimal padding. */}
      <div
        className="relative flex items-center justify-between px-4 pt-2 pb-1 flex-shrink-0">
        <BackPill onClick={onClose} accent="#5DD4FF" testId="aura-close"/>
        <div className="absolute left-0 right-0 flex flex-col items-center leading-none pointer-events-none">
          <h2 className="font-['Cormorant_Garamond'] italic text-[15px]"
            style={{ color: '#CFE9FF', letterSpacing: '0.18em' }}>
            THE AURA CODEX
          </h2>
          <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.22em] mt-0.5 text-white/30">
            {fusedCount}/6 fused
          </p>
        </div>
        <div className="w-9" />
      </div>

      <div className="flex-1 overflow-hidden relative flex flex-col">
        {/* Top spacer — pushes master orb + timeline downward toward
            the master cards (which stay anchored at the bottom). */}
        <div className="flex-1 min-h-[8px]" />

        {/* Master Aura artifact — adopts alarming pillar's palette and
            pulses strongly when a codex alarm is firing. Otherwise falls
            back to the standard `masterPalette` and gentle warning pulse. */}
        <div className="relative flex flex-col items-center pt-0 pb-0">
          <div className="relative flex items-center justify-center"
               style={{ width: 124, height: 124 }}>
          {/* Master orb wrapper — Playstore-safe pulse: transform + opacity
              only (no `filter`). When an alarm is active the orb takes on
              that pillar's colour so it *becomes* the pillar visually. */}
          {(() => {
            const alarmPillar = alarmPillarId
              ? PILLARS.find((p) => p.id === alarmPillarId)
              : null;
            const orbPalette = alarmPillar ? alarmPillar.palette : masterPalette;
            const isAlarming = !!alarmPillar;
            return (
              <>
                <motion.div
                  animate={
                    isAlarming
                      ? { opacity: [1, 0.6, 1], scale: [1, 1.08, 1] }
                      : masterWarning
                      ? { scale: [1, 1.04, 1] }
                      : { scale: 1 }
                  }
                  transition={{ duration: isAlarming ? 0.9 : 2.4, repeat: (isAlarming || masterWarning) ? Infinity : 0, ease: 'easeInOut' }}
                  style={{ willChange: (isAlarming || masterWarning) ? 'opacity, transform' : 'auto' }}>
                  <AuraArtifact
                    tier={isAlarming ? 'resonance' : tier}
                    volume={volume}
                    size={124}
                    palette={orbPalette}
                    warning={!!masterWarning || isAlarming}
                    warningPalette={isAlarming ? alarmPillar.palette : masterWarning?.pillar?.palette}
                  />
                </motion.div>
                {/* Alarming pillar label sits over the orb — bright white
                    so it's readable against any pillar palette. */}
                {isAlarming && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <p className="font-['Cinzel'] uppercase text-[15px]"
                      style={{
                        color: '#FFFFFF',
                        letterSpacing: '0.32em',
                        /* Compensate for trailing letter-spacing so the text
                           is visually centered, not just geometrically. */
                        paddingLeft: '0.32em',
                        textShadow: '0 0 12px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.85)',
                      }}>
                      {alarmPillar.label}
                    </p>
                  </div>
                )}
              </>
            );
          })()}
            {/* 60-s countdown text — appears in the master orb whenever
                a pillar's window is about to close (and it's unfused). */}
            {masterWarning && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="font-['Outfit'] uppercase tracking-[0.3em] text-[8px]"
                  style={{
                    color: masterWarning.pillar.palette.accent,
                    textShadow: `0 0 6px ${masterWarning.pillar.palette.halo}`,
                  }}>
                  {masterWarning.pillar.label}
                </p>
                <p className="font-['Cormorant_Garamond'] italic font-semibold"
                  style={{
                    color: '#FFFFFF',
                    fontSize: 26,
                    lineHeight: 1,
                    textShadow: `0 0 14px ${masterWarning.pillar.palette.halo}`,
                  }}>
                  {String(masterWarning.secondsLeft).padStart(2, '0')}
                </p>
                <p className="font-['Outfit'] uppercase tracking-[0.22em] text-[7px] mt-0.5"
                  style={{ color: masterWarning.pillar.palette.accent }}>
                  seconds
                </p>
              </div>
            )}
          </div>
          <p className="mt-6 font-['Cormorant_Garamond'] italic text-center px-6"
            style={{ color: 'rgba(207,233,255,0.55)', fontSize: 9 }}>
            {masterWarning
              ? `${masterWarning.pillar.label} window is closing — hold to fuse.`
              : tier === 'divine'
                ? 'The Ascension is locked. Stand in your radiance.'
                : tier === 'resonance'
                  ? 'Resonance stabilised — finish the cycle.'
                  : tier === 'awakened'
                    ? 'Vitality awakened. Keep fuelling the core.'
                    : 'The vessel is dormant. Fuse a pillar to begin.'}
          </p>
        </div>

        {/* Timeline of Ascension */}
        <div className="relative px-6 pb-1">
          <div
            aria-hidden
            className="absolute"
            style={{
              left: '50%', top: 0, bottom: 0,
              width: 2,
              transform: 'translateX(-50%)',
              background: 'linear-gradient(180deg, rgba(93,212,255,0.05) 0%, rgba(93,212,255,0.85) 18%, rgba(93,212,255,0.85) 82%, rgba(93,212,255,0.05) 100%)',
              boxShadow: '0 0 8px rgba(93,212,255,0.55), 0 0 20px rgba(93,212,255,0.25)',
            }}
          />
          {/* Kinetic surge */}
          <AnimatePresence>
            {surge && (
              <motion.span
                key={surge.key}
                aria-hidden
                initial={{ top: `calc(${(surge.idx + 0.5) * 16}% + 0px)`, opacity: 1 }}
                animate={{ top: '-180px', opacity: [1, 1, 0] }}
                transition={{ duration: 0.9, ease: [0.5, 0, 0.2, 1] }}
                className="absolute pointer-events-none"
                style={{
                  left: '50%', transform: 'translateX(-50%)',
                  width: 8, height: 22, borderRadius: 4,
                  background: 'linear-gradient(180deg, #FFFFFF 0%, #5DD4FF 60%, transparent 100%)',
                  boxShadow: '0 0 14px rgba(93,212,255,0.9), 0 0 28px rgba(93,212,255,0.55)',
                }}
              />
            )}
          </AnimatePresence>

          <div className="relative z-[1] flex flex-col gap-1 items-center">
            {pillarStatus.map(({ pillar, ps, enabled, warning }) => (
              <div key={pillar.id} className="relative w-full max-w-xs flex justify-center">
                <PillarNode
                  pillar={pillar}
                  fused={!!ps.fused}
                  enabled={enabled}
                  warning={warning}
                  windowLabel={fmtWindow(ps.window)}
                  onToggleFuse={handleToggleFuse}
                  onLabelTap={(pl) => setOpenPillar(pl.id)}
                />
                {/* RIGHT — pillar role description, taps open the Energy Box. */}
                <button
                  type="button"
                  onClick={() => setOpenPillar(pillar.id)}
                  className="absolute left-1/2 ml-12 top-1/2 -translate-y-1/2 text-left active:scale-95 transition-transform"
                  data-testid={`pillar-role-${pillar.id}`}>
                  <p className="font-['Cormorant_Garamond'] italic"
                    style={{
                      color: ps.fused ? '#CFE9FF' : 'rgba(255,255,255,0.78)',
                      fontSize: 13,
                      lineHeight: 1.1,
                    }}>
                    {pillar.role}
                  </p>
                  <p className="font-['Outfit'] uppercase tracking-[0.18em] mt-0.5"
                    style={{ color: 'rgba(255,255,255,0.32)', fontSize: 7.5 }}>
                    Tap to inscribe
                  </p>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom spacer — symmetrical to the top one, so the master orb
            + timeline sit centred between the header and the master cards. */}
        <div className="flex-1 min-h-[8px]" />

        {/* 6 Pillar cards — premium aura balls in a clean 3×2 grid */}
        <div className="px-3"
          style={{ paddingBottom: 'calc(56px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))', boxSizing: 'border-box' }}>
          <div className="grid grid-cols-3 gap-2">
            {pillarStatus.map(({ pillar, ps, warning }) => (
              <PillarCard
                key={pillar.id}
                pillar={pillar}
                fused={!!ps.fused}
                taskCount={(ps.tasks || []).length}
                warning={warning}
                alarming={alarmPillarId === pillar.id}
                onClick={setOpenPillar}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Energy Box panel */}
      <AnimatePresence>
        {openPillarData && (
          <EnergyBox
            pillar={openPillarData}
            state={openPillarState}
            onUpdate={(next) => updatePillar(openPillarData.id, next)}
            onClose={() => setOpenPillar(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuraDashboard;
